import { createTheme } from '@mantine/core';
/**
 * ============================================================================
 * RELEVA.AI MANTINE THEME
 * ============================================================================
 *
 * TYPOGRAPHY: Inter - weight 400 for headings (lighter, airier)
 * PRIMARY COLOR: Amethyst - shade 7 (less contrast, softer)
 *
 * FONT SETUP - Add to index.html <head>:
 *   <link rel="preconnect" href="https://fonts.googleapis.com">
 *   <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
 *   <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
 *
 * ============================================================================
 */
const colors = {
  // Primary: Amethyst-based scale
  primary: [
    '#F5F0FF', // 0 - lightest
    '#EBE0FF', // 1
    '#D4C2FF', // 2
    '#BEA3FF', // 3
    '#A880FF', // 4
    '#904DFF', // 5 - BRAND AMETHYST
    '#7B3EE0', // 6
    '#6B2DC0', // 7 - NEW PRIMARY SHADE (lighter)
    '#5A1FA6', // 8
    '#4A1A8A', // 9 - was primary (too dark)
  ],
  // Secondary: #9d4edd (amethyst purple)
  secondary: [
    '#f9f0fd',
    '#f3e1fb',
    '#e7c3f7',
    '#dba5f3',
    '#cf87ef',
    '#9d4edd', // 5 - base
    '#8a44c4',
    '#773aaa',
    '#643091',
    '#512677',
  ],
  // Accent: #ff66c4 (flamingo pink)
  accent: [
    '#fff0f8',
    '#ffe1f1',
    '#ffc3e3',
    '#ffa5d5',
    '#ff87c7',
    '#ff66c4', // 5 - base
    '#e65ab0',
    '#cc4e9c',
    '#b34288',
    '#993674',
  ],
  // Success: green
  success: [
    '#e6fcf5',
    '#c3fae8',
    '#96f2d7',
    '#63e6be',
    '#38d9a9',
    '#20c997', // 5 - base
    '#12b886',
    '#0ca678',
    '#099268',
    '#087f5b',
  ],
  // Warning: orange
  warning: [
    '#fff9db',
    '#fff3bf',
    '#ffec99',
    '#ffe066',
    '#ffd43b',
    '#fcc419', // 5 - base
    '#fab005',
    '#f59f00',
    '#f08c00',
    '#e67700',
  ],
  // Danger: red
  danger: [
    '#fff5f5',
    '#ffe3e3',
    '#ffc9c9',
    '#ffa8a8',
    '#ff8787',
    '#ff6b6b', // 5 - base
    '#fa5252',
    '#f03e3e',
    '#e03131',
    '#c92a2a',
  ],
  // Info: blue
  info: [
    '#e7f5ff',
    '#d0ebff',
    '#a5d8ff',
    '#74c0fc',
    '#4dabf7',
    '#339af0', // 5 - base
    '#228be6',
    '#1c7ed6',
    '#1971c2',
    '#1864ab',
  ],
  // Dark: #3A1A4B (indigo) at shade 8
  dark: [
    '#f4f1f5',
    '#e0d6e3',
    '#c7b8cd',
    '#ae9ab7',
    '#957ca1',
    '#7c5e8b',
    '#634a70',
    '#4a3655',
    '#3A1A4B', // 8 - brand indigo
    '#2a1337',
  ],
};
export const theme = createTheme({
  colors,
  primaryColor: 'primary',
  primaryShade: 7, // LIGHTER - was 9
  // Typography: Inter - LIGHTER weight
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  fontFamilyMonospace: "SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  headings: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontWeight: '400', // LIGHTER - was 500
  },
  body: {
    color: ['#f1f3f5'],
  },
  defaultRadius: 'sm',
  components: {
    Button: {
      defaultProps: {
        size: 'sm',
      },
      styles: {
        label: {
          fontFamily: 'inherit',
        },
      },
      vars: (theme, props) => {
        if (props.size === 'xs') {
          return {
            root: {
              '--button-fz': 'var(--mantine-font-size-sm)',
            },
          };
        }
        if (props.size === 'sm' || props.size === undefined) {
          return {
            root: {
              '--button-fz': 'var(--mantine-font-size-sm)',
            },
          };
        }
        return { root: {} };
      },
    },
    TextInput: {
      defaultProps: {
        size: 'sm',
      },
    },
    Select: {
      defaultProps: {
        size: 'sm',
      },
    },
    NumberInput: {
      defaultProps: {
        size: 'sm',
      },
    },
    Textarea: {
      defaultProps: {
        size: 'sm',
      },
    },
    DateInput: {
      defaultProps: {
        size: 'sm',
      },
    },
    DatePickerInput: {
      defaultProps: {
        size: 'sm',
      },
    },
    Modal: {
      defaultProps: {
        centered: true,
      },
    },
    // Mantine 9 keeps hidden content mounted inside React's `Activity` by default,
    // which runs every effect cleanup when a tab or panel is hidden and re-runs the
    // effects when it is shown again. Imperative editors do not survive that round
    // trip: @monaco-editor/react disposes its editor on cleanup and never re-creates
    // it, so returning to a tab with a code editor crashed the page ("InstantiationService
    // has been disposed"). 'display-none' is the Mantine 8 behaviour every form here
    // was written against: hidden content stays mounted with its effects alive.
    Tabs: {
      defaultProps: {
        keepMountedMode: 'display-none',
      },
    },
    Collapse: {
      defaultProps: {
        keepMountedMode: 'display-none',
      },
    },
    Accordion: {
      defaultProps: {
        keepMountedMode: 'display-none',
      },
    },
    NavLink: {
      defaultProps: {
        variant: 'light',
        color: 'primary',
      },
      styles: {
        root: {
          borderRadius: 'var(--mantine-radius-sm)',
          margin: '2px 0',
        },
      },
    },
    Switch: {
      styles: {
        track: {
          cursor: 'pointer',
        },
      },
    },
    Grid: {
      styles: {
        root: {
          overflow: 'hidden',
        },
      },
    },
    Badge: {
      styles: {
        label: {
          fontWeight: 'normal',
        },
      },
    },
  },
});
